import { useEffect, useMemo, useRef, useState } from "react";
import type { DeskFeed, DeskStory } from "@chonburi/shared";
import {
  pickTranslationCandidates,
  translateMissing,
  type ClientTranslation,
} from "../lib/clientTranslate";

/**
 * The desk's own translation pass, in the reader's browser.
 *
 * The API could not render some foreign headlines — its address is
 * throttled by the public translation services — so the browser is asked to
 * try, and the desk fills the world compartment when it succeeds. The pass
 * runs after the desk is already on screen: the mayor should not wait for
 * a translation to read the state of his town, and the headlines that
 * arrive are added to a desk that is already complete.
 */

export interface DeskTranslationState {
  /** Every story the desk can show, with the renderings the browser managed. */
  stories: DeskStory[];
  /** How many headlines the browser rendered in Thai, per source language. */
  byLanguage: Record<string, number>;
  /** True while the browser is still translating. */
  pending: boolean;
}

export function useDeskTranslation(feed: DeskFeed | null, now: Date): DeskTranslationState {
  const [result, setResult] = useState<ClientTranslation | null>(null);
  const [pending, setPending] = useState(false);

  // The clock ticks every minute, and a translation pass must not restart
  // with it: the ranking is decided once, when the feed arrives.
  const nowRef = useRef(now);
  useEffect(() => {
    nowRef.current = now;
  }, [now]);

  const base = useMemo(
    () => [...(feed?.features ?? []), ...(feed?.untranslated ?? [])],
    [feed],
  );

  useEffect(() => {
    if (!feed) return;
    let cancelled = false;
    const candidates = pickTranslationCandidates(base, nowRef.current);
    if (!candidates.length) return;

    setPending(true);
    translateMissing(candidates)
      .then((out) => {
        if (cancelled) return;
        setResult(out);
        setPending(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feed, base]);

  const stories = useMemo(() => {
    if (!result) return base;
    const byId = new Map(result.stories.map((s) => [s.id, s]));
    return base.map((s) => byId.get(s.id) ?? s);
  }, [base, result]);

  return {
    stories,
    byLanguage: result?.byLanguage ?? {},
    pending,
  };
}
